import { v4 as uuidv4 } from "uuid";
import { updateBigQueryData } from "../../../../api/postCall/updateBigQueryData";
import { NycbsPharmaOrderKeys } from "../../../../enums/nycbsPharmaOrder";

/**
 * Parameters passed to {@link onSubmissionSave} for persisting submission summary updates.
 */
interface OnSubmissionSaveParams {
  data: any;
  key: string;
  poc: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
  /**
   * When true (compact UI outside pharma STP file), `update_data` omits
   * `second_stp_status` and `response_status`.
   */
  omitHiddenWorkflowFields?: boolean;
  /** Persisted as `response_status` when case status is shown in the modal. */
  caseStatus?: string;
  /** Persisted as `second_stp_status` when workflow fields are shown in the modal. */
  secondStpStatus?: string;
  /** Persisted as `non_epa_required` when the Delivery tab shows call fields. */
  callRequired?: string;
  /** Persisted as `non_epa_reason` (empty when call is not required). */
  callReason?: string;
  /** Persisted as `route_via`. */
  routeVia: string;
}

/**
 * Persists submission summary fields for a pharma worklist order via BigQuery update.
 *
 * @param params - Row data, key, POC, route via, second STP status, call fields, optional case status, and callbacks
 */
export const onSubmissionSave = async (params: OnSubmissionSaveParams) => {
  const {
    data,
    key,
    poc,
    omitHiddenWorkflowFields,
    caseStatus,
    secondStpStatus,
    callRequired,
    callReason,
    routeVia,
    onSuccess,
    onError,
  } = params;

  try {
    const identifier =
      data?.[NycbsPharmaOrderKeys.Identifier] || data?.identifier;
    if (!identifier) {
      throw new Error("Identifier not found in data");
    }

    if (!key) {
      throw new Error("CMM Result Key not found");
    }

    const update_data: Record<string, string> = {
      covermymed_id: key,
      poc,
      route_via: routeVia,
    };

    if (callRequired !== undefined) {
      update_data.non_epa_required = callRequired;
      update_data.non_epa_reason = callReason ?? "";
    }

    if (!omitHiddenWorkflowFields) {
      update_data.second_stp_status = secondStpStatus ?? "";
      update_data.response_status = caseStatus ?? "";
    }

    const payload = {
      filters: {
        identifier: identifier,
      },
      update_data,
      request_id: uuidv4(),
    };
    await updateBigQueryData(payload);

    onSuccess();
  } catch (error) {
    console.error("Error in submission save:", error);
    onError(error as Error);
  }
};
